import { http } from "@/lib/api";

const B = "/bills-native";

// Every call resolves to { ok, status, data } so callers never parse axios errors.
async function call(method, url, body, params, headers) {
  try {
    const r = await http.request({ method, url: `${B}${url}`, data: body, params, headers });
    return { ok: true, status: r.status, data: r.data };
  } catch (e) {
    const status = e?.response?.status ?? 0;
    const detail = e?.response?.data?.detail;
    return { ok: false, status, data: detail && typeof detail === "object" ? detail : { message: detail } };
  }
}

export const billsApi = {
  readiness: () => call("get", "/readiness"),
  authority: (cid) => call("get", `/candidates/${encodeURIComponent(cid)}/authority`),
  start: (cid) => call("post", `/candidates/${encodeURIComponent(cid)}/start`),
  bill: (bid) => call("get", `/bills/${bid}`),
  ingredients: (q) => call("get", "/ingredients", undefined, { q, limit: 8 }),
  setIngredient: (bid, lid, ingredient_id) => call("post", `/bills/${bid}/lines/${lid}/ingredient`, { ingredient_id }),
  setSource: (bid, lid, changes) => call("post", `/bills/${bid}/lines/${lid}/source`, changes),
  setConversion: (bid, lid, body) => call("post", `/bills/${bid}/lines/${lid}/conversion`, body),
  confirm: (bid, body) => call("post", `/bills/${bid}/confirm`, body),
  classifyCharge: (bid, chargeId, kind) => call("post", `/bills/${bid}/charges/${encodeURIComponent(chargeId)}/classify`, { kind }),
  landedCostOverride: (bid, body) => call("post", `/bills/${bid}/landed-cost-override`, body),
  decideVendor: (bid, body) => call("post", `/bills/${bid}/vendor`, body),
};

// Route not mounted (flag OFF) → FastAPI's plain {"detail":"Not Found"}; no error_code.
// Phase 7A read models
export const billsRead = {
  list: (params) => call("get", "/bills", undefined, params),
  homeSummary: (params) => call("get", "/home-summary", undefined, params),
  vendors: (params) => call("get", "/vendors", undefined, params),
  vendorDetail: (id) => call("get", `/vendors/${encodeURIComponent(id)}`),
  ingredientPrices: (id, params) => call("get", `/ingredient-prices/${encodeURIComponent(id)}`, undefined, params),
  priceReference: (params) => call("get", "/price-reference", undefined, params),
  detail: (billId) => call("get", `/bills/${encodeURIComponent(billId)}/detail`),
  landedCostPolicy: (params) => call("get", "/landed-cost-policy", undefined, params),
  setLandedCostPolicy: (body) => call("post", "/landed-cost-policy", body),
  // one Idempotency-Key per operator correction action; retries of that action reuse it
  correct: (billId, body, key) => call("post", `/bills/${encodeURIComponent(billId)}/corrections`, body, undefined, { "Idempotency-Key": key }),
  // canonical supplier-Bill VOID governance — the backend owns maker/checker, settlement and downstream effects
  requestVoid: (billId, body, key) => call("post", `/bills/${encodeURIComponent(billId)}/void-requests`, body, undefined, { "Idempotency-Key": key }),
  approveVoid: (billId, body, key) => call("post", `/bills/${encodeURIComponent(billId)}/void-requests/approve`, body, undefined, { "Idempotency-Key": key }),
  rejectVoid: (billId, body, key) => call("post", `/bills/${encodeURIComponent(billId)}/void-requests/reject`, body, undefined, { "Idempotency-Key": key }),
};

export const isFeatureOff = (res) => res.status === 404 && !res.data?.error_code;

// 1OS Bills · Expenses — operational facts only. Accounts owns physical cash counts and every cash calculation.
export const expensesApi = {
  meta: () => call("get", "/expenses/meta"),
  list: (params) => call("get", "/expenses", undefined, params),
  get: (id) => call("get", `/expenses/${encodeURIComponent(id)}`),
  create: (body) => call("post", "/expenses", body),
  void: (id, reason) => call("post", `/expenses/${encodeURIComponent(id)}/void`, { reason }),
};
